import { forwardRef, type PropsWithChildren } from 'react';

import { CategoricalBarChartRoot } from './CategoricalBarChartRoot.js';
import { CategoryAxis } from './slots/CategoryAxis.js';
import { CategoricalBarChartDownloadCSVSlot } from './slots/DownloadCSV.js';
import { Legend } from './slots/Legend.js';
import { ThresholdIndicator } from './slots/ThresholdIndicator.js';
import { CategoricalBarChartToolbarSlot } from './slots/Toolbar.js';
import { Tooltip } from './slots/Tooltip.js';
import { ValueAxis } from './slots/ValueAxis.js';
import type {
  CategoricalBarChartProps,
  CategoricalBarChartRef,
} from './types/categorical-bar-chart.js';
import { Intent } from '../../core/slots/Intent/Intent.js';
import { EmptyStateSlot } from '../core/slots/chart-states/EmptyStateSlot.js';
import { ErrorStateSlot } from '../core/slots/chart-states/ErrorStateSlot.js';
import { ColorRule } from '../core/slots/ColorRule.js';
import { isFeatureEnabled } from '../core/utils/is-feature-enabled.js';
import { CategoricalBarToXY } from '../xy-chart-adoption/categorical-bar/CategoricalBarToXY.js';
import type { _XYChartAdoptionProps } from '../xy-chart-adoption/shared/types/props.js';

export const CategoricalBarChartSwitcher = forwardRef<
  CategoricalBarChartRef,
  PropsWithChildren<CategoricalBarChartProps & _XYChartAdoptionProps>
>((props, forwardedRef) => {
  const { __useXYAdoption, ...rest } = props;
  if (__useXYAdoption ?? isFeatureEnabled('categoricalBarToXY')) {
    return <CategoricalBarToXY {...rest} ref={forwardedRef} />;
  }
  return <CategoricalBarChartRoot {...rest} ref={forwardedRef} />;
});

(
  CategoricalBarChartSwitcher as typeof CategoricalBarChartSwitcher & {
    displayName: string;
  }
).displayName = 'CategoricalBarChart';

/**
 * The `CategoricalBarChart` visually displays the frequencies or values of
 * distinct categories using rectangular bars. Each bar's length or height
 * corresponds to the quantity or value associated with a specific category,
 * making it easy to compare the relative differences among categories.
 * @public
 */
export const CategoricalBarChart = Object.assign(CategoricalBarChartSwitcher, {
  Legend,
  Tooltip,
  CategoryAxis,
  ValueAxis,
  ThresholdIndicator,
  Intent,
  ColorRule,
  EmptyState: EmptyStateSlot,
  ErrorState: ErrorStateSlot,
  Toolbar: CategoricalBarChartToolbarSlot,
  DownloadCSV: CategoricalBarChartDownloadCSVSlot,
});
